/**
 * Deterministic representative-slice sampling. Pure and unit-tested.
 *
 * Slices are ordered by projecting ImagePositionPatient onto the slice normal
 * (the same convention as `assembleVolumeFromSlices`: the normal is column 3
 * of the parsed orientation matrix, computed from ImageOrientationPatient at
 * parse time). Absolute coordinates are never mapped to anatomy.
 * Works for CT (HU) and MR (window-relative signal).
 */
import { vec3, type ReadonlyMat3, type ReadonlyVec3 } from 'gl-matrix';

export const SAMPLE_PERCENTILES = [10, 30, 50, 70, 90] as const;

/** CT voxels above this HU count as patient/table rather than air. */
const BODY_HU_THRESHOLD = -500;
/** A slice needs at least this fraction of non-air voxels to be analysed. */
const MIN_BODY_FRACTION = 0.05;
/**
 * MR has no calibrated scale, so "not air" is relative to the display window
 * the slice is rendered with: a voxel counts as signal when it renders above
 * this fraction of the window (≈ 10 % grey).
 */
const MR_SIGNAL_WINDOW_FRACTION = 0.1;
/** |cos| of the angle between slice normal and the patient S–I axis (≈ 25°). */
const AXIAL_MIN_COS = 0.9;

export interface OrientedSlice {
    readonly imagePosition: ReadonlyVec3;
    readonly imageOrientation: ReadonlyMat3;
}

export function orderAlongSliceNormal<S extends OrientedSlice>(
    slices: ReadonlyArray<S>
): S[] {
    if (slices.length === 0) {
        return [];
    }
    const o = slices[0].imageOrientation;
    const normal = vec3.fromValues(o[6], o[7], o[8]);
    return slices
        .map((slice) => ({ slice, z: vec3.dot(normal, slice.imagePosition) }))
        .sort((a, b) => a.z - b.z)
        .map((s) => s.slice);
}

/**
 * True when the slice normal is within ~25° of the patient superior–inferior
 * axis. The classifier and the percentile sampling both assume an axial
 * stack; a sagittal or coronal slice shows several regions at once.
 */
export function isAxialStack(orientation: ReadonlyMat3): boolean {
    const normal = vec3.fromValues(
        orientation[6],
        orientation[7],
        orientation[8]
    );
    const length = vec3.length(normal);
    return length > 0 && Math.abs(orientation[8]) / length >= AXIAL_MIN_COS;
}

/** MR signal threshold for a window (level/width in stored pixel units). */
export function mrSignalThreshold(level: number, window: number): number {
    return level - window / 2 + MR_SIGNAL_WINDOW_FRACTION * window;
}

/**
 * Fraction of sampled voxels above `threshold` (every 4th voxel). The
 * default is the CT air threshold in HU.
 */
export function bodyFraction(
    pixels: ArrayLike<number>,
    threshold: number = BODY_HU_THRESHOLD
): number {
    let body = 0;
    let total = 0;
    for (let i = 0; i < pixels.length; i += 4) {
        total += 1;
        if (pixels[i] > threshold) {
            body += 1;
        }
    }
    return total === 0 ? 0 : body / total;
}

export function hasEnoughBody(
    pixels: ArrayLike<number>,
    threshold: number = BODY_HU_THRESHOLD
): boolean {
    return bodyFraction(pixels, threshold) >= MIN_BODY_FRACTION;
}

export interface SampledSlice<S> {
    readonly percentile: number;
    readonly sliceIndex: number;
    readonly slice: S;
}

/**
 * Pick the slice at each percentile of the ordered stack. An unusable slice
 * (burned-in annotation, almost no anatomy) is replaced by the nearest usable
 * one, searching toward the middle first; a percentile with no usable slice
 * nearby is dropped. Each slice is used at most once. The viewer always uses
 * {@link SAMPLE_PERCENTILES}; other positions are for the offline evaluation.
 */
export function pickRepresentativeSlices<S>(
    ordered: ReadonlyArray<S>,
    isUsable: (slice: S) => boolean,
    percentiles: ReadonlyArray<number> = SAMPLE_PERCENTILES
): SampledSlice<S>[] {
    const n = ordered.length;
    if (n === 0) {
        return [];
    }
    const maxShift = Math.max(1, Math.floor(n * 0.1));
    const used = new Set<number>();
    const picks: SampledSlice<S>[] = [];

    for (const percentile of percentiles) {
        const target = Math.round((percentile / 100) * (n - 1));
        const towardMiddle = target < (n - 1) / 2 ? 1 : -1;
        const candidates = [target];
        for (let d = 1; d <= maxShift; d += 1) {
            candidates.push(
                target + towardMiddle * d,
                target - towardMiddle * d
            );
        }
        const index = candidates.find(
            (i) => i >= 0 && i < n && !used.has(i) && isUsable(ordered[i])
        );
        if (index !== undefined) {
            used.add(index);
            picks.push({
                percentile,
                sliceIndex: index,
                slice: ordered[index],
            });
        }
    }
    return picks;
}
